import { useMemo, useState } from 'react';
import { ArchiveRestore, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
    useBehaviorBank,
    useCreateBehaviorBankEntry,
    useUpdateBehaviorBankEntry,
    useDeleteBehaviorBankEntry,
    useRestoreBehaviorBankEntry,
    type BehaviorBankEntry,
    type BehaviorBankWritePayload,
} from '@/api/behaviorBank';
import { useBehaviorTypes } from '@/api/behaviorTypes';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';

export function BehaviorBankPanel() {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [search, setSearch] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [viewing, setViewing] = useState<BehaviorBankEntry | null>(null);
    const [editing, setEditing] = useState<BehaviorBankEntry | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmArchive, setConfirmArchive] = useState<BehaviorBankEntry | null>(null);

    const { data: types } = useBehaviorTypes();
    const { data, isLoading, isError, error } = useBehaviorBank({
        behaviorTypeId: typeFilter || undefined,
        includeArchived: showArchived,
    });
    const createM = useCreateBehaviorBankEntry();
    const updateM = useUpdateBehaviorBankEntry();
    const deleteM = useDeleteBehaviorBankEntry();
    const restoreM = useRestoreBehaviorBankEntry();

    const activeTypes = useMemo(
        () => (types ?? [])
            .filter((t) => t.status === 'Active')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [types],
    );

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((r) => r.name.toLowerCase().includes(q));
    }, [data, search]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-text-subtle">
                    Practice-wide starting-point behaviors. Copying one to a client duplicates the
                    fields — subsequent edits don't flow back.
                </p>
                {canEdit && (
                    <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                        <Plus className="w-3.5 h-3.5" /> Add Behavior
                    </Button>
                )}
            </div>

            <Card className="p-0 overflow-hidden mt-3">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div className="relative flex-1 min-w-[220px] max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name…"
                            aria-label="Search behaviors by name"
                            className="pl-8 h-8 py-1"
                        />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        aria-label="Filter by behavior type"
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All types</option>
                        {activeTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-text-muted select-none cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        Show archived
                    </label>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'behavior' : 'behaviors'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load behavior bank</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No behaviors match the current filters.' : 'No behaviors in the bank yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold">Type</th>
                                    <th className="px-3 py-2 font-bold">Function</th>
                                    <th className="px-3 py-2 font-bold">Definition</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.id}
                                        onClick={() => setViewing(r)}
                                        className={`border-b border-border last:border-b-0 transition-colors cursor-pointer ${r.archived ? 'opacity-60 hover:bg-surface-muted' : 'hover:bg-surface-muted'
                                            }`}
                                    >
                                        <td className="px-3 py-2 font-medium">
                                            {r.name}
                                            {r.archived && (
                                                <span className="ml-2 text-2xs uppercase tracking-wider text-text-subtle">Archived</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{r.behaviorType || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.function || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">
                                            <span className="line-clamp-1">{r.definition || '—'}</span>
                                        </td>
                                        {canEdit && (
                                            <td
                                                className="px-3 py-2 text-right"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {r.archived ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="xs"
                                                        aria-label={`Restore ${r.name}`}
                                                        disabled={restoreM.isPending}
                                                        onClick={() => restoreM.mutate(r.id)}
                                                    >
                                                        <ArchiveRestore className="w-3 h-3" />
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Edit ${r.name}`}
                                                            onClick={() => setEditing(r)}
                                                            className="mr-1"
                                                        >
                                                            <Pencil className="w-3 h-3" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Archive ${r.name}`}
                                                            onClick={() => setConfirmArchive(r)}
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {viewing && <BehaviorDetailDialog entry={viewing} onClose={() => setViewing(null)} />}

            {(creating || editing) && (
                <BehaviorBankEntryDialog
                    initial={editing}
                    activeTypes={activeTypes}
                    onClose={() => {
                        setCreating(false);
                        setEditing(null);
                    }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirmArchive && (
                <ArchiveConfirmDialog
                    row={confirmArchive}
                    onClose={() => setConfirmArchive(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmArchive.id);
                        setConfirmArchive(null);
                    }}
                />
            )}
        </div>
    );
}

function BehaviorDetailDialog({
    entry,
    onClose,
}: {
    entry: BehaviorBankEntry;
    onClose: () => void;
}) {
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),44rem)]" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{entry.name}</DialogTitle>
                    {entry.behaviorType && (
                        <p className="text-xs text-text-subtle mt-1">{entry.behaviorType}</p>
                    )}
                </DialogHeader>
                <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5 text-sm">
                    <Row label="Definition" value={entry.definition} />
                    <Row label="Topography" value={entry.topography} />
                    <Row label="Function" value={entry.function} />
                    <Row label="Antecedent" value={entry.antecedent} />
                    <Row label="Replacement" value={entry.replacement} />
                    <Row label="Proactive" value={entry.proactive} />
                    <Row label="Reactive" value={entry.reactive} />
                    <Row label="Baseline" value={entry.baseline} />
                    <Row label="Baseline date" value={entry.baselineDate ?? ''} />
                    <Row label="Severity / intensity" value={entry.severityIntensity} />
                    <Row label="Data collection method" value={entry.dataCollectionMethod} />
                    <Row label="Reduction goal" value={entry.reductionGoal} />
                </dl>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <>
            <dt className="text-xs font-bold text-text-muted uppercase tracking-wider pt-1">{label}</dt>
            <dd className="text-text whitespace-pre-wrap">{value || <span className="text-text-subtle">—</span>}</dd>
        </>
    );
}

function BehaviorBankEntryDialog({
    initial,
    activeTypes,
    onClose,
    onSubmit,
}: {
    initial: BehaviorBankEntry | null;
    activeTypes: { id: string; name: string }[];
    onClose: () => void;
    onSubmit: (p: BehaviorBankWritePayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [behaviorTypeId, setTypeId] = useState(initial?.behaviorTypeId ?? '');
    const [definition, setDefinition] = useState(initial?.definition ?? '');
    const [topography, setTopography] = useState(initial?.topography ?? '');
    const [fn, setFn] = useState(initial?.function ?? '');
    const [antecedent, setAntecedent] = useState(initial?.antecedent ?? '');
    const [replacement, setReplacement] = useState(initial?.replacement ?? '');
    const [proactive, setProactive] = useState(initial?.proactive ?? '');
    const [reactive, setReactive] = useState(initial?.reactive ?? '');
    const [baseline, setBaseline] = useState(initial?.baseline ?? '');
    const [reductionGoal, setGoal] = useState(initial?.reductionGoal ?? '');
    const [severityIntensity, setSeverity] = useState(initial?.severityIntensity ?? '');
    const [baselineDate, setBaselineDate] = useState(initial?.baselineDate ?? '');
    const [dataCollectionMethod, setDcm] = useState(initial?.dataCollectionMethod ?? '');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                behaviorTypeId: behaviorTypeId || null,
                definition: definition.trim(),
                topography: topography.trim(),
                function: fn.trim(),
                antecedent: antecedent.trim(),
                replacement: replacement.trim(),
                proactive: proactive.trim(),
                reactive: reactive.trim(),
                baseline: baseline.trim(),
                reductionGoal: reductionGoal.trim(),
                severityIntensity: severityIntensity.trim(),
                baselineDate: baselineDate || null,
                dataCollectionMethod: dataCollectionMethod.trim(),
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),48rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit Behavior' : 'Add Behavior'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1 -mx-1">
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Name" required error={err ?? undefined}>
                            {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                        </FormField>
                        <FormField label="Type">
                            {(id) => (
                                <select
                                    id={id}
                                    value={behaviorTypeId ?? ''}
                                    onChange={(e) => setTypeId(e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">— None —</option>
                                    {activeTypes.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                    </div>
                    <FormField label="Definition">
                        {(id) => <Textarea id={id} value={definition} onChange={(e) => setDefinition(e.target.value)} rows={2} />}
                    </FormField>
                    <div className="grid grid-cols-3 gap-x-4">
                        <FormField label="Severity / intensity">
                            {(id) => <Input id={id} value={severityIntensity} onChange={(e) => setSeverity(e.target.value)} />}
                        </FormField>
                        <FormField label="Baseline date">
                            {(id) => <Input id={id} type="date" min="1900-01-01" max={toIsoDate(new Date())} value={baselineDate ?? ''} onChange={(e) => setBaselineDate(e.target.value)} />}
                        </FormField>
                        <FormField label="Data collection method">
                            {(id) => <Input id={id} value={dataCollectionMethod} onChange={(e) => setDcm(e.target.value)} />}
                        </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Topography">
                            {(id) => <Textarea id={id} value={topography} onChange={(e) => setTopography(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Function">
                            {(id) => <Textarea id={id} value={fn} onChange={(e) => setFn(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Antecedent">
                            {(id) => <Textarea id={id} value={antecedent} onChange={(e) => setAntecedent(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Replacement">
                            {(id) => <Textarea id={id} value={replacement} onChange={(e) => setReplacement(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Proactive">
                            {(id) => <Textarea id={id} value={proactive} onChange={(e) => setProactive(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Reactive">
                            {(id) => <Textarea id={id} value={reactive} onChange={(e) => setReactive(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Baseline">
                            {(id) => <Textarea id={id} value={baseline} onChange={(e) => setBaseline(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Reduction goal">
                            {(id) => <Textarea id={id} value={reductionGoal} onChange={(e) => setGoal(e.target.value)} rows={2} />}
                        </FormField>
                    </div>
                    <DialogFooter className="mt-2">
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ArchiveConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: BehaviorBankEntry;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Archive behavior?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Archive <span className="font-medium text-text">{row.name}</span> from the
                    behavior bank? It will be hidden from the default view, but client behaviors
                    already copied from this entry keep their own data and aren't affected.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                setErr(extractErrorMessage(e, 'Could not archive.'));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Archiving…' : 'Archive'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
